import { getErrorMessage } from '@features/shared/api/client'
import { useCallback, useState } from 'react'
import { useSettingsStore } from '@features/settings/stores/useSettingsStore'
import { useToast } from '@features/shared/components/Toast/ToastContext'
import { buildEmailBody, trimLineEnd, type SharePayload } from '../logic/sharePayload'

export interface ShareActions {
  /** The link is being built (a view's pending changes are being saved first). */
  isPreparing: boolean
  copyLink: () => Promise<void>
  shareByEmail: () => Promise<void>
  shareInTeams: () => Promise<void>
  /** The browser's own share sheet, where it has one. */
  shareAnotherWay: () => Promise<void>
  canShareAnotherWay: boolean
}

function openExternalLink(url: string) {
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * The ways to send a link, around `prepare`, which builds what is sent when
 * the user picks one (null when there's nothing to share; it says why).
 */
export function useShareActions(
  prepare: () => Promise<SharePayload | null>,
  /** The copy toast's message, such as "The view link is ready to share." */
  copiedMessage: string
): ShareActions {
  const [isPreparing, setIsPreparing] = useState(false)
  const { addToast } = useToast()
  const emailGreetingBreakpoints = useSettingsStore((state) => state.emailGreetingBreakpoints)
  const defaultEmailSignOff = useSettingsStore((state) => state.defaultEmailSignOff)

  const preparePayload = useCallback(async (): Promise<SharePayload | null> => {
    setIsPreparing(true)
    try {
      return await prepare()
    } catch (error) {
      addToast({
        type: 'error',
        title: "Couldn't prepare link",
        message: getErrorMessage(error),
      })
      return null
    } finally {
      setIsPreparing(false)
    }
  }, [addToast, prepare])

  const copyLink = useCallback(async () => {
    const payload = await preparePayload()
    if (!payload) return
    try {
      await navigator.clipboard.writeText(trimLineEnd(payload.url))
      addToast({ type: 'success', title: 'Link copied', message: copiedMessage })
    } catch {
      addToast({
        type: 'error',
        title: "Couldn't copy link",
        message: 'Your browser blocked clipboard access.',
      })
    }
  }, [addToast, copiedMessage, preparePayload])

  const shareByEmail = useCallback(async () => {
    const payload = await preparePayload()
    if (!payload) return
    const subject = encodeURIComponent(trimLineEnd(payload.subject))
    const body = encodeURIComponent(
      buildEmailBody(payload, emailGreetingBreakpoints, defaultEmailSignOff)
    )
    openExternalLink(`mailto:?subject=${subject}&body=${body}`)
  }, [defaultEmailSignOff, emailGreetingBreakpoints, preparePayload])

  const shareInTeams = useCallback(async () => {
    const payload = await preparePayload()
    if (!payload) return
    const href = encodeURIComponent(trimLineEnd(payload.url))
    const msgText = encodeURIComponent(trimLineEnd(payload.message))
    window.open(
      `https://teams.microsoft.com/share?href=${href}&msgText=${msgText}`,
      '_blank',
      'noopener,noreferrer'
    )
  }, [preparePayload])

  const shareAnotherWay = useCallback(async () => {
    const payload = await preparePayload()
    if (!payload) return
    try {
      await navigator.share({
        title: trimLineEnd(payload.subject),
        text: trimLineEnd(payload.message),
        url: trimLineEnd(payload.url),
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      addToast({
        type: 'error',
        title: "Couldn't share",
        message: getErrorMessage(error, 'Try another way to share.'),
      })
    }
  }, [addToast, preparePayload])

  return {
    isPreparing,
    copyLink,
    shareByEmail,
    shareInTeams,
    shareAnotherWay,
    canShareAnotherWay: typeof navigator.share === 'function',
  }
}
